import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import apiService from "../services/api";
import toast from "react-hot-toast";
import {
  User,
  Calendar,
  DollarSign,
  FileText,
  Settings,
  LogOut,
  Upload,
  Camera,
  TrendingUp,
  Building,
  Phone,
  Mail,
  Star,
  Clock,
  Bell,
  Home,
  Download,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import Button from "../components/ui/Button";

const EmployeeDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [showImageUpload, setShowImageUpload] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }
    fetchEmployeeData();
  }, [user, navigate]);

  const fetchEmployeeData = async () => {
    try {
      setLoading(true);
      // Fetch employee data based on user ID
      const employeeData = await apiService.getEmployeeProfile(user.id);
      setEmployee(employeeData);

      // Fetch dashboard data
      const dashboard = await apiService.getEmployeeDashboard();
      setDashboardData(dashboard);
    } catch (error) {
      console.error("Error fetching employee data:", error);
      toast.error("Failed to load employee data");
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedFile) {
      toast.error("Please select an image first");
      return;
    }

    try {
      setUploadingImage(true);
      const formData = new FormData();
      formData.append("image", selectedFile);
      formData.append("employeeId", employee.id);

      const response = await apiService.uploadEmployeeImage(formData);

      if (response.success) {
        toast.success("Profile image updated successfully!");
        setEmployee((prev) => ({ ...prev, profileImage: response.imageUrl }));
        setShowImageUpload(false);
        setSelectedFile(null);
        setPreviewUrl(null);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
    } finally {
      setUploadingImage(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        // 5MB limit
        toast.error("Image size should be less than 5MB");
        return;
      }

      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      setSelectedFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreviewUrl(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogout = () => {
    logout();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="w-full">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              Employee Portal
            </h1>
            <p className="mt-2 text-gray-600">Welcome back, {employee?.name}</p>
          </div>

          <div className="flex items-center gap-3">
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <Bell className="h-5 w-5" />
            </button>
            <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
              <Settings className="h-5 w-5" />
            </button>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>

        {/* Profile Section */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
            {/* Profile Image */}
            <div className="relative">
              <div className="relative">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-100 flex items-center justify-center border-4 border-white shadow-md">
                  {employee?.profileImage ? (
                    <img
                      src={employee.profileImage}
                      alt={employee?.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <User className="h-16 w-16 text-gray-400" />
                  )}
                </div>
                <button
                  onClick={() => setShowImageUpload(true)}
                  className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                >
                  <Camera className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* Profile Info */}
            <div className="flex-1 text-center lg:text-left">
              <div className="flex items-center justify-center lg:justify-start space-x-2 mb-2">
                <h2 className="text-2xl font-bold text-gray-900">
                  {employee?.name}
                </h2>
                <div className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">
                  Active
                </div>
              </div>
              <p className="text-lg text-gray-600 mb-4">
                {employee?.designation}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <Building className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{employee?.department}</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{employee?.email}</span>
                </div>
                <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600">{employee?.phone}</span>
                </div>
              </div>

              <div className="flex flex-wrap justify-center lg:justify-start gap-2">
                <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                  Employee ID: {employee?.employeeId}
                </div>
                <div className="bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                  Joined: {new Date(employee?.joiningDate).toLocaleDateString()}
                </div>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-2 gap-4 w-full lg:w-auto">
              <div className="bg-blue-50 text-blue-900 p-4 rounded-xl text-center border border-blue-100">
                <DollarSign className="h-6 w-6 mx-auto mb-2 text-blue-600" />
                <div className="text-2xl font-bold">
                  {dashboardData?.salary || "₹0"}
                </div>
                <div className="text-sm opacity-80">Monthly Salary</div>
              </div>
              <div className="bg-green-50 text-green-900 p-4 rounded-xl text-center border border-green-100">
                <Calendar className="h-6 w-6 mx-auto mb-2 text-green-600" />
                <div className="text-2xl font-bold">
                  {dashboardData?.attendance || 0}
                </div>
                <div className="text-sm opacity-80">Days Present</div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="border-b border-gray-200">
            <nav className="flex space-x-8 px-6 overflow-x-auto">
              {[
                { id: "overview", label: "Overview", icon: Home },
                { id: "attendance", label: "Attendance", icon: Calendar },
                { id: "payroll", label: "Payroll", icon: DollarSign },
                { id: "leaves", label: "Leaves", icon: FileText },
                { id: "performance", label: "Performance", icon: TrendingUp },
                { id: "documents", label: "Documents", icon: FileText },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? "border-blue-500 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  <tab.icon className="h-4 w-4" />
                  <span>{tab.label}</span>
                </button>
              ))}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === "overview" && (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Quick Actions */}
                <div className="lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Quick Actions
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {[
                      { icon: Clock, label: "Clock In/Out", color: "blue" },
                      {
                        icon: Calendar,
                        label: "Request Leave",
                        color: "green",
                      },
                      {
                        icon: FileText,
                        label: "View Payslip",
                        color: "purple",
                      },
                      {
                        icon: Settings,
                        label: "Update Profile",
                        color: "orange",
                      },
                    ].map((action) => (
                      <button
                        key={action.label}
                        className={`bg-${action.color}-50 text-${action.color}-700 p-4 rounded-xl hover:shadow-md transition-all border border-${action.color}-100 flex flex-col items-center justify-center text-center`}
                      >
                        <action.icon
                          className={`h-6 w-6 mb-2 text-${action.color}-600`}
                        />
                        <div className="text-sm font-medium">
                          {action.label}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recent Activity */}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">
                    Recent Activity
                  </h3>
                  <div className="space-y-3">
                    {[
                      {
                        action: "Clocked in",
                        time: "9:00 AM",
                        status: "success",
                      },
                      {
                        action: "Leave approved",
                        time: "Yesterday",
                        status: "success",
                      },
                      {
                        action: "Payslip generated",
                        time: "2 days ago",
                        status: "info",
                      },
                    ].map((activity, index) => (
                      <div
                        key={index}
                        className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg border border-gray-100"
                      >
                        <div
                          className={`w-2 h-2 rounded-full ${
                            activity.status === "success"
                              ? "bg-green-500"
                              : "bg-blue-500"
                          }`}
                        ></div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-gray-900">
                            {activity.action}
                          </div>
                          <div className="text-xs text-gray-500">
                            {activity.time}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {activeTab === "attendance" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 text-blue-900 p-6 rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-blue-600 text-sm font-medium">
                          This Month
                        </p>
                        <p className="text-3xl font-bold mt-1">22</p>
                        <p className="text-blue-600 text-sm mt-1">
                          Days Present
                        </p>
                      </div>
                      <Calendar className="h-12 w-12 text-blue-200" />
                    </div>
                  </div>
                  <div className="bg-green-50 text-green-900 p-6 rounded-xl border border-green-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-green-600 text-sm font-medium">
                          Attendance Rate
                        </p>
                        <p className="text-3xl font-bold mt-1">95%</p>
                        <p className="text-green-600 text-sm mt-1">Excellent</p>
                      </div>
                      <TrendingUp className="h-12 w-12 text-green-200" />
                    </div>
                  </div>
                  <div className="bg-purple-50 text-purple-900 p-6 rounded-xl border border-purple-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-purple-600 text-sm font-medium">
                          Overtime
                        </p>
                        <p className="text-3xl font-bold mt-1">12</p>
                        <p className="text-purple-600 text-sm mt-1">Hours</p>
                      </div>
                      <Clock className="h-12 w-12 text-purple-200" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "payroll" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Salary Breakdown
                    </h4>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Basic Salary</span>
                        <span className="font-medium">
                          ₹{employee?.basicSalary || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">HRA</span>
                        <span className="font-medium">
                          ₹{employee?.salaryStructure?.hra || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">DA</span>
                        <span className="font-medium">
                          ₹{employee?.salaryStructure?.da || 0}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Conveyance</span>
                        <span className="font-medium">
                          ₹{employee?.salaryStructure?.conveyance || 0}
                        </span>
                      </div>
                      <hr className="my-3 border-gray-100" />
                      <div className="flex justify-between font-semibold text-gray-900">
                        <span>Gross Salary</span>
                        <span>₹{dashboardData?.grossSalary || 0}</span>
                      </div>
                    </div>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h4 className="text-lg font-semibold text-gray-900 mb-4">
                      Recent Payslips
                    </h4>
                    <div className="space-y-3">
                      {[1, 2, 3].map((month) => (
                        <div
                          key={month}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <div>
                            <div className="font-medium text-gray-900">
                              March 2024
                            </div>
                            <div className="text-sm text-gray-500">
                              Net: ₹{dashboardData?.netSalary || 0}
                            </div>
                          </div>
                          <button className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors">
                            <Download className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "leaves" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                  <div className="bg-green-50 text-green-900 p-6 rounded-xl border border-green-100 text-center">
                    <div className="text-3xl font-bold mb-1">15</div>
                    <div className="text-green-700 text-sm font-medium">
                      Casual Leave
                    </div>
                  </div>
                  <div className="bg-blue-50 text-blue-900 p-6 rounded-xl border border-blue-100 text-center">
                    <div className="text-3xl font-bold mb-1">12</div>
                    <div className="text-blue-700 text-sm font-medium">
                      Sick Leave
                    </div>
                  </div>
                  <div className="bg-purple-50 text-purple-900 p-6 rounded-xl border border-purple-100 text-center">
                    <div className="text-3xl font-bold mb-1">30</div>
                    <div className="text-purple-700 text-sm font-medium">
                      Earned Leave
                    </div>
                  </div>
                  <div className="bg-orange-50 text-orange-900 p-6 rounded-xl border border-orange-100 text-center">
                    <div className="text-3xl font-bold mb-1">5</div>
                    <div className="text-orange-700 text-sm font-medium">
                      Used This Year
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "performance" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-yellow-50 text-yellow-900 p-6 rounded-xl border border-yellow-100 text-center">
                    <div className="text-3xl font-bold mb-1">4.8</div>
                    <div className="text-yellow-700 text-sm font-medium mb-2">
                      Performance Rating
                    </div>
                    <div className="flex justify-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          className="h-4 w-4 fill-yellow-400 text-yellow-400"
                        />
                      ))}
                    </div>
                  </div>
                  <div className="bg-green-50 text-green-900 p-6 rounded-xl border border-green-100 text-center">
                    <div className="text-3xl font-bold mb-1">95%</div>
                    <div className="text-green-700 text-sm font-medium">
                      Goal Achievement
                    </div>
                  </div>
                  <div className="bg-blue-50 text-blue-900 p-6 rounded-xl border border-blue-100 text-center">
                    <div className="text-3xl font-bold mb-1">12</div>
                    <div className="text-blue-700 text-sm font-medium">
                      Projects Completed
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "documents" && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    {
                      name: "Employment Contract",
                      type: "PDF",
                      size: "2.3 MB",
                    },
                    {
                      name: "Salary Slip - March 2024",
                      type: "PDF",
                      size: "1.1 MB",
                    },
                    { name: "Performance Review", type: "PDF", size: "856 KB" },
                    {
                      name: "Training Certificate",
                      type: "PDF",
                      size: "3.2 MB",
                    },
                    { name: "Insurance Policy", type: "PDF", size: "4.1 MB" },
                    { name: "Company Handbook", type: "PDF", size: "5.7 MB" },
                  ].map((doc, index) => (
                    <div
                      key={index}
                      className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="bg-red-50 p-2 rounded-lg">
                          <FileText className="h-6 w-6 text-red-600" />
                        </div>
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">
                            {doc.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {doc.type} • {doc.size}
                          </div>
                        </div>
                        <button className="text-blue-600 hover:text-blue-800 p-2 hover:bg-blue-50 rounded-lg transition-colors">
                          <Download className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Upload Modal */}
      {showImageUpload && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Update Profile Picture
            </h3>

            <div className="space-y-4">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="image-upload"
                />
                <label
                  htmlFor="image-upload"
                  className="cursor-pointer w-full block"
                >
                  {previewUrl ? (
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="w-32 h-32 rounded-full mx-auto object-cover"
                    />
                  ) : (
                    <div className="space-y-2">
                      <Upload className="h-12 w-12 text-gray-400 mx-auto" />
                      <p className="text-gray-600">Click to upload image</p>
                      <p className="text-sm text-gray-500">
                        JPG, PNG up to 5MB
                      </p>
                    </div>
                  )}
                </label>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowImageUpload(false)}
                  className="flex-1 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleImageUpload}
                  disabled={!selectedFile || uploadingImage}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {uploadingImage ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeDashboard;
